import { useRef, useState, useMemo } from "react";
import { formatPercent, formatCurrency } from "utils/formatNumber";
import useColumnSelector from "hooks/useColumnSelector";
import ReactTable from "react-table-6";
import { Card, Row, Col, Table } from "react-bootstrap";
import ReactTablePagination from "components/custom/growth/ProductsCVTablePagination";
import TrComponent from "components/custom/merchandise/TrComponent";
import ExpanderCell from "components/custom/merchandise/cells/ExpanderCell";
import { useFetch } from "hooks/api";
import moment from "moment";
import Loading from "components/core/blocks/Loading";
import { Link } from "react-router-dom";

const ExpandedContent = ({ row }) => {
  const { data: couponStats, isLoading: isCouponStatsLoading } = useFetch(
    ["couponMetrics", row?.original.id],
    `/merchandise/coupons_metrics/?report_id=${row?.original.id}`,
    {},
    {
      select: (data) => data?.data,
    },
  );

  if (isCouponStatsLoading) {
    return <Loading height={"300px"} fullPage={false} />;
  }

  if (couponStats?.results?.length === 0) {
    return (
      <div className="p5- m-5 fw-bold text-center">
        <em>There are no associated coupons for this product</em>
      </div>
    );
  }

  const stats = couponStats.results[0];

  const redemtionRate = stats.clips ? stats.redemptions / stats.clips : 0;
  const salesPerRedemption = stats.redemptions ? stats.sales / stats.redemptions : 0;
  const discountPercentOfSales = stats.sales ? stats.total_discount / stats.sales : 0;
  const costPerRedemption = stats.redemptions ? stats.budget_spent / stats.redemptions : 0;

  return (
    <div style={{ backgroundColor: "#efefef" }}>
      <Card className="fs-5 mb-0 p-3">
        <Card.Body>
          <Row>
            <Col xs={12}>
              <h6>Coupon Metrics</h6>
            </Col>

            <Col xs={12} className="pt-3">
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th className="text-center">Budget</th>
                    <th className="text-center">Budget % Used</th>
                    <th className="text-center">Budget Remaining</th>
                    <th className="text-center">Budget Spent</th>
                    <th className="text-center">Clips</th>
                    <th className="text-center">Redemptions</th>
                    <th className="text-center">Sales</th>
                    <th className="text-center">Total Discount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="text-center">{formatCurrency(stats.budget, row?.original.marketplace)}</td>
                    <td className="text-center">{formatPercent(stats.budget_percentage_used / 100.0, 2)}</td>
                    <td className="text-center">{formatCurrency(stats.budget_remaining, row?.original.marketplace)}</td>
                    <td className="text-center">{formatCurrency(stats.budget_spent, row?.original.marketplace)}</td>
                    <td className="text-center">{stats.clips}</td>
                    <td className="text-center">{stats.redemptions}</td>
                    <td className="text-center">{formatCurrency(stats.sales, row?.original.marketplace)}</td>
                    <td className="text-center">{formatCurrency(stats.total_discount, row?.original.marketplace)}</td>
                  </tr>
                </tbody>
              </Table>
            </Col>

            <Col xs={12} className="pt-5">
              <h6>Coupon Insights</h6>
            </Col>

            <Col xs={12} className="pt-3">
              <Table striped bordered hover>
                <tbody>
                  <tr>
                    <th className="text-left" style={{ width: "200px" }}>
                      Redemption Rate
                    </th>
                    <td className="text-left">
                      The coupon achieved a <span className="fw-bold">{formatPercent(redemtionRate, 0)}</span>{" "}
                      redemption rate, showing the proportion of clips that led to redemptions.
                    </td>
                  </tr>
                  <tr>
                    <th className="text-left">Sales per Redemption</th>
                    <td className="text-left">
                      On average, each coupon redemption resulted in{" "}
                      <span className="fw-bold">{formatCurrency(salesPerRedemption, row?.original.marketplace)}</span>{" "}
                      in sales, indicating the revenue impact per use.
                    </td>
                  </tr>
                  <tr>
                    <th className="text-left">Discount % of Sales</th>
                    <td className="text-left">
                      The discount provided accounts for{" "}
                      <span className="fw-bold">{formatPercent(discountPercentOfSales, 0)}</span> of the total sales,
                      representing the portion of sales value reduced by the coupon.
                    </td>
                  </tr>
                  <tr>
                    <th className="text-left">Cost Per Redenption</th>
                    <td className="text-left">
                      <div>
                        So far,{" "}
                        <span className="fw-bold">{formatPercent(stats.budget_percentage_used / 100.0, 2)}</span> of the
                        budget has been spent, equating to{" "}
                        <span className="fw-bold">{formatCurrency(costPerRedemption, row?.original.marketplace)}</span>{" "}
                        per redemption in cost.
                      </div>
                    </td>
                  </tr>
                </tbody>
              </Table>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </div>
  );
};

const makeExpanderCell = (accessor, setExpanded) => {
  return ({ row, value }) => {
    const id = accessor(row?._original);
    return <ExpanderCell row={row} value={id} isExpanded={value} setIsExpanded={setExpanded} />;
  };
};

const CouponReport = ({ data, marketPlace, asinReport }) => {
  const [colToggle] = useState();
  const [expanded, setExpanded] = useState({});

  const tableData = useMemo(() => {
    if (!data) return [];

    return data?.results.map((row) => ({
      ...row,
      isExpanded: row.id === expanded ? row.id : null,
    }));
  }, [data, expanded]);

  const tableRef = useRef();
  const [columns, columnSelectorProps] = useColumnSelector(() => {
    const columns = [
      {
        id: "expander",
        Header: "",
        accessor: "isExpanded",
        width: 50,
        Cell: makeExpanderCell((row) => row.id, setExpanded),
      },
      {
        id: "Coupon Title",
        label: "Coupon Title",
        Header: () => <span>Coupon Title</span>,
        headerClassName: "wordwrap",
        headerStyle: { textAlign: "left" },
        style: { textAlign: "left" },
        width: 400,
        accessor: "name",
        Cell: (props) => {
          return (
            <div>
              <div className="fw-bold">
                <Link to={`/user/merchandising/coupons/${props.original.id}`}>{props.value}</Link>
              </div>
              <div style={{ fontSize: "11px" }}>
                <em>
                  {props.original?.website_message} - ({props.original.coupon_id})
                </em>
              </div>
            </div>
          );
        },
      },
      {
        id: "StartTime",
        label: "Start Time",
        Header: () => <span>Start Time</span>,
        headerClassName: "wordwrap",
        headerStyle: { textAlign: "left" },
        style: { textAlign: "left" },
        accessor: "start_time",
        Cell: (props) => {
          return <div>{moment(props.value).format("MMM DD YYYY")}</div>;
        },
      },
      {
        id: "EndTime",
        label: "EndTime",
        Header: () => <span>End Time</span>,
        headerClassName: "wordwrap",
        headerStyle: { textAlign: "left" },
        style: { textAlign: "left" },
        accessor: "end_time",
        Cell: (props) => {
          return <div>{moment(props.value).format("MMM DD YYYY")}</div>;
        },
      },
      {
        id: "DiscountAmount",
        label: "DiscountAmount",
        Header: () => <span>Discount Amount</span>,
        headerClassName: "wordwrap",
        headerStyle: { textAlign: "left" },
        style: { textAlign: "left" },
        accessor: "discount_amount",
        Cell: (props) => {
          if (props.original.discount_type === "PERCENT_OFF_LIST_PRICE") {
            return <div>{formatPercent(parseFloat(props.value) / 100.0, 0)}</div>;
          } else {
            return <div>{formatCurrency(props.value, marketPlace)}</div>;
          }
        },
      },
    ];

    const asinColumns = [
      {
        id: "Units",
        label: "Units",
        Header: () => <span>Units</span>,
        headerClassName: "wordwrap",
        headerStyle: { textAlign: "left" },
        style: { textAlign: "left" },
        accessor: "order_info.count",
        Cell: (props) => {
          return <div>{props.value}</div>;
        },
      },
      {
        id: "Sales",
        label: "Sales",
        Header: () => <span>Promo Sales</span>,
        headerClassName: "wordwrap",
        headerStyle: { textAlign: "left" },
        style: { textAlign: "left" },
        accessor: "order_info.sales",
        Cell: (props) => {
          return <div>{formatCurrency(props.value, marketPlace)}</div>;
        },
      },
      {
        id: "PromoDiscount",
        label: "PromoDiscount",
        Header: () => <span>Promo Discount</span>,
        headerClassName: "wordwrap",
        headerStyle: { textAlign: "left" },
        style: { textAlign: "left" },
        accessor: "order_info.promo_discount",
        Cell: (props) => {
          return <div>{formatCurrency(props.value, marketPlace)}</div>;
        },
      },
      {
        id: "discount_percent_of_sales",
        label: "discount_percent_of_sales",
        Header: () => <span>Discount % of Sales</span>,
        headerClassName: "wordwrap",
        headerStyle: { textAlign: "left" },
        style: { textAlign: "left" },
        accessor: (props) =>
          props.order_info.before_sales ? props.order_info.promo_discount / props.order_info.before_sales : 0,
        Cell: (props) => {
          return <div>{formatPercent(parseFloat(props.value), 2)}</div>;
        },
      },
    ];

    const moreColumns = [
      {
        id: "Sales",
        label: "Sales",
        Header: () => <span>Sales</span>,
        headerClassName: "wordwrap",
        headerStyle: { textAlign: "left" },
        style: { textAlign: "left" },
        accessor: "metrics.sales",
        Cell: (props) => {
          return <div>{formatCurrency(parseFloat(props.value), marketPlace)}</div>;
        },
      },
      {
        id: "Clips",
        label: "Clips",
        Header: () => <span>Clips</span>,
        headerClassName: "wordwrap",
        headerStyle: { textAlign: "left" },
        style: { textAlign: "left" },
        accessor: "metrics.clips",
        Cell: (props) => {
          return <div>{props.value}</div>;
        },
      },
      {
        id: "Redemptions",
        label: "Redemptions",
        Header: () => <span>Redemptions</span>,
        headerClassName: "wordwrap",
        headerStyle: { textAlign: "left" },
        style: { textAlign: "left" },
        accessor: "metrics.redemptions",
        Cell: (props) => {
          return <div>{props.value}</div>;
        },
      },
      {
        id: "redemption_rate",
        label: "redemption_rate",
        Header: () => <span>Redemption Rate</span>,
        headerClassName: "wordwrap",
        headerStyle: { textAlign: "left" },
        style: { textAlign: "left" },
        accessor: "metrics.redemption_rate",
        Cell: (props) => {
          return <div>{formatPercent(parseFloat(props.value), 1)}</div>;
        },
      },
      {
        id: "sales_per_redemption",
        label: "sales_per_redemption",
        Header: () => <span>Sales per Redemption</span>,
        headerClassName: "wordwrap",
        headerStyle: { textAlign: "left" },
        style: { textAlign: "left" },
        accessor: "metrics.sales_per_redemption",
        Cell: (props) => {
          return <div>{formatCurrency(parseFloat(props.value), marketPlace)}</div>;
        },
      },
      {
        id: "discount_percent_of_sales",
        label: "discount_percent_of_sales",
        Header: () => <span>ACOS</span>,
        headerClassName: "wordwrap",
        headerStyle: { textAlign: "left" },
        style: { textAlign: "left" },
        accessor: "metrics.discount_percent_of_sales",
        Cell: (props) => {
          return <div>{formatPercent(parseFloat(props.value), 2)}</div>;
        },
      },
      {
        id: "budget_efficiency",
        label: "budget_efficiency",
        Header: () => <span>Cost per Redemption</span>,
        headerClassName: "wordwrap",
        headerStyle: { textAlign: "left" },
        style: { textAlign: "left" },
        accessor: "metrics.cost_per_redemption",
        Cell: (props) => {
          return <div>{formatCurrency(parseFloat(props.value), marketPlace)}</div>;
        },
      },
    ];

    if (asinReport) {
      columns.push(...asinColumns);
    } else {
      columns.push(...moreColumns);
    }

    return columns;
  }, [colToggle]);

  if (data?.results?.length === 0) {
    return (
      <div className="p5- m-5 fw-bold text-center">
        <em>There are no associated coupons for this product</em>
      </div>
    );
  }

  return (
    <ReactTable
      ref={tableRef}
      data={tableData}
      className="coupon-table"
      PaginationComponent={(props) => {
        return (
          <ReactTablePagination
            {...props}
            table={"coupon-table"}
            showFilters={false}
            style={{ marginBottom: "2rem" }}
            carouselLayout={true}
            hideFilters={true}
            columnSelectorProps={columnSelectorProps}
            hideSearch={true}
          />
        );
      }}
      TrComponent={TrComponent}
      getTrProps={(table, row) => {
        const expandedChildren = <ExpandedContent row={row} />;
        return { row, expandedChildren };
      }}
      columns={columns}
      defaultPageSize={20}
      NoDataComponent={() => null}
      minRows={0}
      showPaginationTop
      showPaginationBottom={false}
      filterable={false}
    />
  );
};

export default CouponReport;
